import { cycloneApiClient } from '@/shared/api/core/axios-instance';

import type { AssetSearchResultItem } from '@/entities/asset-search';
import { mapAssetSearchResultToSearchResult } from '@/entities/asset-search';
import type { FilterState } from '@/entities/discovery/domain/types';
import type { SearchResult } from '@/entities/search';
import { transformOpenMetadataToSearchResult } from '@/entities/search';

import { searchOpenMetadata } from './openmetadataSearchApi';

type ExperimentSearchParams = {
  query?: string;
  size?: number;
};

export type IntegrationSearchParams = {
  query?: string;
  size?: number;
  cursor?: string | null;
  assetTypes?: string[];
  rangeStart?: string | null;
  rangeEnd?: string | null;
  projectId?: number | null;
  experimentId?: number | null;
  runId?: number | null;
  sortBy?: 'name' | 'created_at' | 'updated_at';
  sortOrder?: 'asc' | 'desc';
};

export type IntegrationSearchResponse = {
  results: AssetSearchResultItem[];
  totalCount: number;
  nextCursor: string | null;
  hasMore: boolean;
};

const searchExperimentData = async (query: string, size: number = 5): Promise<SearchResult[]> => {
  try {
    const params: ExperimentSearchParams = {
      size,
    };

    if (query.trim()) {
      params.query = query;
    }

    const response = await cycloneApiClient.get<IntegrationSearchResponse>('/search/integration', {
      params,
    });

    return response.data.results.map(mapAssetSearchResultToSearchResult);
  } catch (error) {
    console.error('[searchExperimentData] 실험 데이터 조회 실패:', error);
    return [];
  }
};

const searchExperimentDataWithPagination = async (
  params: IntegrationSearchParams
): Promise<IntegrationSearchResponse> => {
  try {
    const response = await cycloneApiClient.get<IntegrationSearchResponse>('/search/integration', {
      params,
    });

    return response.data;
  } catch (error) {
    console.error('[searchExperimentDataWithPagination] 실험 데이터 조회 실패:', error);
    return {
      results: [],
      totalCount: 0,
      nextCursor: null,
      hasMore: false,
    };
  }
};

const SUGGESTION_SIZE = 5;

export const searchIntegration = async (query: string, filters: FilterState): Promise<SearchResult[]> => {
  const trimmedQuery = query.trim();

  if (!trimmedQuery) return [];

  try {
    const [experimentResults, omResponse] = await Promise.all([
      searchExperimentData(trimmedQuery, SUGGESTION_SIZE),
      searchOpenMetadata(trimmedQuery, filters, 0, SUGGESTION_SIZE).catch(() => ({
        hits: { hits: [] },
        aggregations: null,
      })),
    ]);

    const omResults = omResponse.hits.hits.map(transformOpenMetadataToSearchResult);

    const allResults = [...experimentResults, ...omResults];

    const sortedResults = allResults.sort((a, b) => b.title.localeCompare(a.title));

    return sortedResults;
  } catch (error) {
    console.error('[searchIntegration] 통합 검색 실패:', error);
    return [];
  }
};

export const searchIntegrationWithPagination = async (
  params: IntegrationSearchParams
): Promise<IntegrationSearchResponse> => {
  return searchExperimentDataWithPagination(params);
};
